// Error reporting (Sentry) — what may leave the system. Used as `beforeSend` / `beforeBreadcrumb` by the API, the
// worker and the web app (docs/DECISIONS.md › Operations — error tracking). Reports carry the stack, the route and
// the service / platform; never cookies, auth headers, request bodies, query strings (e-mailed verification / reset /
// invite tokens travel there), IP addresses or any user field but an id.

/** The parts of a Sentry event this module touches (structurally compatible with `@sentry/core`'s `Event`). */
export interface ScrubbableEvent {
  request?: {
    url?: string;
    query_string?: unknown;
    cookies?: unknown;
    data?: unknown;
    headers?: Record<string, string>;
  };
  user?: { [key: string]: unknown };
  breadcrumbs?: ScrubbableBreadcrumb[];
}

export interface ScrubbableBreadcrumb {
  data?: { [key: string]: unknown };
}

/** Headers that may carry credentials or identify a person. */
const SENSITIVE_HEADER = /^(authorization|cookie|set-cookie|x-forwarded-for|x-real-ip|forwarded|x-device-token|.*token.*)$/i;

/** Drops the query string and fragment of a URL. */
export function stripUrl(url: string): string {
  const cut = url.search(/[?#]/);
  return cut < 0 ? url : url.slice(0, cut);
}

export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(crumb: T): T {
  const data = crumb.data;
  if (data) {
    // fetch / xhr / navigation / http breadcrumbs
    for (const key of ['url', 'from', 'to']) {
      const v = data[key];
      if (typeof v === 'string') data[key] = stripUrl(v);
    }
  }
  return crumb;
}

export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  const req = event.request;
  if (req) {
    if (req.url) req.url = stripUrl(req.url);
    delete req.query_string;
    delete req.cookies;
    delete req.data;
    if (req.headers) {
      for (const name of Object.keys(req.headers)) if (SENSITIVE_HEADER.test(name)) delete req.headers[name];
    }
  }
  const user = event.user;
  if (user) {
    for (const key of Object.keys(user)) if (key !== 'id') delete user[key];
  }
  if (event.breadcrumbs) for (const crumb of event.breadcrumbs) scrubBreadcrumb(crumb);
  return event;
}

/**
 * A Sentry DSN (`https://<public key>@<host>/<project id>`, optionally with a path prefix before the id) → where its
 * envelopes go. Null for anything that is not a well-formed https DSN (http only for localhost tests).
 */
export function parseSentryDsn(dsn: string): { publicKey: string; projectId: string; envelopeUrl: string } | null {
  let u: URL;
  try {
    u = new URL(dsn);
  } catch {
    return null;
  }
  const local = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  if (u.protocol !== 'https:' && !(local && u.protocol === 'http:')) return null;
  const parts = u.pathname.split('/').filter(Boolean);
  const projectId = parts.pop();
  if (!u.username || !projectId || !/^\d+$/.test(projectId)) return null;
  const prefix = parts.length ? `/${parts.join('/')}` : '';
  return { publicKey: u.username, projectId, envelopeUrl: `${u.protocol}//${u.host}${prefix}/api/${projectId}/envelope/` };
}
